import { fillPath, route } from "./routes.js";
import { z } from "zod";
import { channelDisplayNameSchema, channelShareDecisionStateSchema } from "@zion/sdk-contracts";
//#region src/channel-offers.ts
/**
* `/v1/release/channel-offers` — the private release channels developers have offered to this org,
* and the org's accept and decline (private-release-channels §2c, L-5, R-027).
*
* A developer shares a private channel, or a whole namespace `private/<handle>/*`, with a tenant on
* the CP. The CP carries the share to the tenant's data plane in the desired-state envelope, and
* the operator writes it into core-api's `release.channel_offers`. A tenant admin lists those
* offers here and accepts or declines each one. The decision stays on the data plane, and the
* operator reports it up to the CP. An accepted offer's channels back Customer Channels
* (`createChannelRequestSchema`'s `privateChannelId` in `channels.ts`). The admin who accepts may
* also hold `customer-channel:manage` or `channels:manage`. Then the accept itself creates a
* Customer Channel for each offer channel that no Customer Channel of the org binds yet. A refusal
* skips that channel. An admin can also create one by hand with `privateChannelId`.
*
* Producer: core-api's `ChannelOffersController` in `src/release/` (WS-3). Consumer: the admin app
* in `witness-ai/zion-admin-app`, which builds its offers page from this module alone (L-12), and
* core-api's `zion-dp-org-admin` MCP tools (WS-3b). The path sits outside `/v1/admin`, so it is
* written literally.
*
* Permissions (R-004): `channel-offer:read` on the list, `channel-offer:manage` on accept and
* decline. Every route reads the org from the verified JWT; no route takes an org id.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused. Accept and decline take no body.
*/
/** The offer collection. No parameter. */
const RELEASE_CHANNEL_OFFERS_PATH = "/v1/release/channel-offers";
/**
* One offer, in OpenAPI `{param}` form, keyed by the CP share id. No route answers on this path
* alone; accept and decline hang below it. Fill the two action paths with
* {@link channelOfferAcceptPath} and {@link channelOfferDeclinePath}.
*/
const RELEASE_CHANNEL_OFFER_PATH_TEMPLATE = `${RELEASE_CHANNEL_OFFERS_PATH}/{shareId}`;
/**
* `GET /v1/release/channel-offers`. Every unrevoked offer for the caller's org, newest `offeredAt`
* first, ties broken by `shareId`, each with the org's decision state (`pending` until decided).
* A revoked offer is absent. An org with no offers gets `[]`. Permission `channel-offer:read`.
*/
const LIST_CHANNEL_OFFERS_ROUTE = route("GET", RELEASE_CHANNEL_OFFERS_PATH);
/**
* `POST /v1/release/channel-offers/{shareId}/accept`. Records the org's acceptance; **200** with the
* offer's view, `state: 'accepted'`. A repeat accept of an accepted offer leaves the decision
* unchanged and answers the current view of the offer. Permission `channel-offer:manage`. The
* refusals:
* - 400 when `shareId` is not a uuid;
* - 403 `not_a_user` when the caller is a machine principal;
* - 404 `channel_offer_not_found` when this org holds no unrevoked offer for it, including another
*   org's share, so the route is no existence oracle.
*
* Both coded bodies carry `error` equal to `code`, so `AllExceptionsFilter` of `@zion/sdk-nestjs`,
* the global exception filter of core-api, sends them whole (core-api issue #4509).
*
* The caller may also hold `customer-channel:manage` or `channels:manage`. Then core-api creates
* one Customer Channel for each offer channel that no Customer Channel of the org binds yet. It
* does so on a repeat accept too. Each new channel has these values:
* - the name `Private channel <key>` after the channel's `key`, for example
*   `Private channel p-0b842d78548d`;
* - the ring `edge` and that key;
* - no group;
* - `release_channel:manage` for the caller.
*
* A refusal for one channel skips that channel and leaves the 200 as it is. The refusals:
* - a channel id that `isLowerCaseUuid` in `@zion/sdk-contracts` refuses;
* - a key outside the grammar;
* - the org floor;
* - a name the org already uses;
* - a decline that committed in between.
*
* A 5xx `HttpException` in that step answers with its own status. Any other error, a database error
* included, answers 500. In both cases the decision stays recorded. The response carries the
* offer view alone. Read the new channels from `GET /v1/release/channels`, which needs
* `channels:read`.
*/
const ACCEPT_CHANNEL_OFFER_ROUTE = route("POST", `${RELEASE_CHANNEL_OFFER_PATH_TEMPLATE}/accept`);
/**
* `POST /v1/release/channel-offers/{shareId}/decline`. Records the org's decline; **200** with the
* offer's view, `state: 'declined'`. An org can decline an accepted offer. Each Customer Channel
* bound to its channels then becomes unavailable, and its users fall back to the org default
* (R-019). A channel that another accepted, unrevoked offer also covers stays available.
* Permission `channel-offer:manage`. Same 400, 403 and 404 as accept.
*/
const DECLINE_CHANNEL_OFFER_ROUTE = route("POST", `${RELEASE_CHANNEL_OFFER_PATH_TEMPLATE}/decline`);
/** The route's param schema: the CP share id is a uuid, and anything else is a 400 at core-api. */
const shareIdPathParamSchema = z.uuid();
/**
* Fill {@link ACCEPT_CHANNEL_OFFER_ROUTE}'s path with one share id.
*
* @throws ZodError when `shareId` is not a uuid, before a URL is built.
*/
function channelOfferAcceptPath(shareId) {
	return fillPath(ACCEPT_CHANNEL_OFFER_ROUTE.path, { shareId }, { shareId: shareIdPathParamSchema });
}
/**
* Fill {@link DECLINE_CHANNEL_OFFER_ROUTE}'s path with one share id.
*
* @throws ZodError when `shareId` is not a uuid, before a URL is built.
*/
function channelOfferDeclinePath(shareId) {
	return fillPath(DECLINE_CHANNEL_OFFER_ROUTE.path, { shareId }, { shareId: shareIdPathParamSchema });
}
/**
* One private channel inside an offer, as core-api stores it from the envelope's
* `ChannelOfferChannel` (`@zion/sdk-cp-dp-contracts`), which this mirrors member for member. This
* package does not depend on the CP-DP contract package, so the admin app pulls in no wire code.
*/
const channelOfferChannelViewSchema = z.object({
	/** The CP `release.channels.id`. `createChannelRequestSchema.privateChannelId` takes this value. */
	channelId: z.string(),
	/** `private/<handle>/<name>`. */
	slug: z.string(),
	/** The routing key, `p-` and 12 hex digits. */
	key: z.string(),
	/** The developer's label. */
	displayName: channelDisplayNameSchema,
	description: z.string().nullable()
});
/**
* One offer with the org's decision state — the item of `GET /v1/release/channel-offers` and the
* body of accept and decline.
*
* `ownerHandle` and `ownerEmail` name the developer who shared it, so an admin sees who is offering
* a build before accepting (R-011). `state` is `pending`, `accepted` or `declined`
* (`channelShareDecisionStateSchema` in `@zion/sdk-contracts`).
*/
const channelOfferViewSchema = z.object({
	/** The CP share id; accept and decline name it. */
	shareId: z.string(),
	/** An exact private slug, or `private/<handle>/*`. */
	pattern: z.string(),
	ownerHandle: z.string(),
	ownerEmail: z.string(),
	/** ISO-8601, when the developer created the share. */
	offeredAt: z.string(),
	/** The owner's channels the pattern covers now; a wildcard gains channels as the owner adds them. */
	channels: z.array(channelOfferChannelViewSchema),
	state: channelShareDecisionStateSchema
});
/** `GET /v1/release/channel-offers` response: every unrevoked offer, unpaged. */
const channelOfferListResponseSchema = z.array(channelOfferViewSchema);
/** Accept and decline response: the offer's view with its new state, 200. */
const channelOfferDecisionResponseSchema = channelOfferViewSchema;
/**
* Every route this file declares, folded into `ADMIN_CONTRACT_ROUTE_FAMILIES` under
* `channel-offers`. core-api's `admin-route-census.spec.ts` checks each against its committed
* `openapi.json`, which serves all three.
*/
const channelOffersRoutes = Object.freeze([
	LIST_CHANNEL_OFFERS_ROUTE,
	ACCEPT_CHANNEL_OFFER_ROUTE,
	DECLINE_CHANNEL_OFFER_ROUTE
]);
//#endregion
export { ACCEPT_CHANNEL_OFFER_ROUTE, DECLINE_CHANNEL_OFFER_ROUTE, LIST_CHANNEL_OFFERS_ROUTE, RELEASE_CHANNEL_OFFERS_PATH, RELEASE_CHANNEL_OFFER_PATH_TEMPLATE, channelOfferAcceptPath, channelOfferChannelViewSchema, channelOfferDecisionResponseSchema, channelOfferDeclinePath, channelOfferListResponseSchema, channelOfferViewSchema, channelOffersRoutes };

//# sourceMappingURL=channel-offers.js.map